import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { toast } from 'sonner';
import {
  PencilIcon,
  TrashIcon,
  EyeIcon,
  PlusIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { Invoice, Project, InvoiceGroup } from '../types';
import { apiService } from '../services/apiService';
import { EmailService } from '../services/emailService';
import { InvoicePdfPreviewModal } from './InvoicePdfPreviewModal';

export interface BulkDeleteResult {
  deleted: string[];
  failed: Array<{ id: string; error: string; message: string }>;
  message?: string;
}

export interface BulkStatusResult {
  updated: string[];
  failed: Array<{ id: string; error: string; message: string }>;
  message?: string;
}

interface InvoiceListProps {
  invoices: Invoice[];
  projects?: Project[];
  globalProjectId?: string | null;
  activeProjectName?: string | null;
  canDelete?: boolean;
  onViewInvoice?: (invoice: Invoice) => void;
  onEditInvoice?: (invoice: Invoice) => void;
  onDeleteInvoice?: (invoiceId: string) => void | Promise<void>;
  onBulkDeleteInvoices?: (invoiceIds: string[]) => Promise<BulkDeleteResult | void>;
  onBulkUpdateStatus?: (
    invoiceIds: string[],
    status: 'draft' | 'sent' | 'paid' | 'overdue'
  ) => Promise<BulkStatusResult | void>;
  onBulkAssignProject?: (
    invoiceIds: string[],
    projectId: string | null
  ) => Promise<{ updated: string[]; failed: Array<{ id: string; error: string }>; message: string } | void>;
  onBulkAssignGroups?: (
    invoiceIds: string[],
    groupIds: string[]
  ) => Promise<{ updated: string[]; failed: Array<{ id: string; error: string; message?: string }>; message: string } | void>;
  onAddInvoice?: () => void;
  onMarkAsPaid?: (invoiceId: string) => void;
}

const isInvoiceLocked = (invoice: Invoice) => !!invoice.alreadyPresentedToAeat;

const invoiceNumberLabel = (invoice: Invoice) =>
  invoice.displayNumber || String(invoice.number);

const invoiceSearchText = (invoice: Invoice) =>
  `${invoiceNumberLabel(invoice)} ${invoice.client?.name || ''} ${invoice.description || ''}`.toLowerCase();

const PROJECT_FILTER_CHIPS = [
  { key: 'all', label: 'Todos' },
  { key: 'entik-media', label: 'Entik Media' },
  { key: 'entik-records', label: 'Entik Records' },
  { key: 'kit-digital', label: 'Kit Digital' },
  { key: 'credens-clothing', label: 'Credens' },
  { key: 'conciertos', label: 'Conciertos' },
] as const;

export const InvoiceList: React.FC<InvoiceListProps> = ({
  invoices,
  projects = [],
  globalProjectId = null,
  activeProjectName = null,
  canDelete = false,
  onViewInvoice,
  onEditInvoice,
  onDeleteInvoice,
  onBulkDeleteInvoices,
  onBulkUpdateStatus,
  onBulkAssignProject,
  onBulkAssignGroups,
  onAddInvoice,
  onMarkAsPaid
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterProjectSlug, setFilterProjectSlug] = useState<string>('all');
  const [filterGroupSlug, setFilterGroupSlug] = useState<string>('all');
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[]>([]);
  const [sortBy, setSortBy] = useState<string>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [sendingEmails, setSendingEmails] = useState<Set<string>>(new Set());
  const [emailMessages, setEmailMessages] = useState<Record<string, string>>({});
  const [pdfPreview, setPdfPreview] = useState<{ id: string; number?: string | number } | null>(null);

  const [downloadingPdfId, setDownloadingPdfId] = useState<string | null>(null);

  const handleDownloadPdf = async (invoice: Invoice) => {
    setDownloadingPdfId(invoice.id);
    try {
      const result = await apiService.downloadInvoicePdf(
        invoice.id,
        `factura-${invoiceNumberLabel(invoice)}.pdf`
      );
      if (!result.ok) {
        toast.error(result.message || result.error || 'No se pudo descargar el PDF');
      }
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteFeedback, setDeleteFeedback] = useState<string | null>(null);
  const [deleteErrors, setDeleteErrors] = useState<
    Array<{ id: string; error: string; message: string }>
  >([]);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [statusFeedback, setStatusFeedback] = useState<string | null>(null);
  const [statusErrors, setStatusErrors] = useState<
    Array<{ id: string; error: string; message: string }>
  >([]);
  const [isAssigningProject, setIsAssigningProject] = useState(false);
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const projectDropdownRef = React.useRef<HTMLDivElement>(null);
  const [isAssigningGroup, setIsAssigningGroup] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const groupDropdownRef = React.useRef<HTMLDivElement>(null);

  const emailService = EmailService.getInstance();

  useEffect(() => {
    (async () => {
      const res = await apiService.getInvoiceGroups();
      if (!res.error && res.data?.groups) setInvoiceGroups(res.data.groups);
    })();
  }, []);

  // Close project dropdown on outside click
  useEffect(() => {
    if (!showProjectDropdown) return;
    const handler = (e: MouseEvent) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(e.target as Node)) {
        setShowProjectDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showProjectDropdown]);

  useEffect(() => {
    if (!showGroupDropdown) return;
    const handler = (e: MouseEvent) => {
      if (groupDropdownRef.current && !groupDropdownRef.current.contains(e.target as Node)) {
        setShowGroupDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showGroupDropdown]);

  const projectById = useMemo(() => {
    const map = new Map<string, Project>();
    projects.forEach((p) => map.set(p.id, p));
    return map;
  }, [projects]);

  const showProjectBadges = !globalProjectId;

  const filteredAndSortedInvoices = useMemo(() => {
    return invoices
      .filter(invoice => {
        const haystack = invoiceSearchText(invoice);
        const matchesSearch =
          haystack.includes(searchTerm.toLowerCase()) ||
          invoice.number.toString().includes(searchTerm);

        const matchesStatus = filterStatus === 'all' || invoice.status === filterStatus;

        let matchesProject = true;
        if (!globalProjectId && filterProjectSlug !== 'all') {
          const invProject = invoice.projectId ? projectById.get(invoice.projectId) : null;
          matchesProject = invProject?.slug === filterProjectSlug;
        }

        let matchesGroup = true;
        if (filterGroupSlug !== 'all') {
          const slugs = (invoice.groups || []).map((g) => g.slug);
          matchesGroup = slugs.includes(filterGroupSlug);
        }

        return matchesSearch && matchesStatus && matchesProject && matchesGroup;
      })
      .sort((a, b) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortBy) {
          case 'number':
            aValue = a.number;
            bValue = b.number;
            break;
          case 'date':
            aValue = new Date(a.date as string | Date).getTime() || 0;
            bValue = new Date(b.date as string | Date).getTime() || 0;
            break;
          case 'total':
            aValue = a.total;
            bValue = b.total;
            break;
          case 'client':
            aValue = a.client.name;
            bValue = b.client.name;
            break;
          default:
            aValue = new Date(a.date as string | Date).getTime() || 0;
            bValue = new Date(b.date as string | Date).getTime() || 0;
        }

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        }
        return aValue < bValue ? 1 : -1;
      });
  }, [invoices, searchTerm, filterStatus, filterProjectSlug, filterGroupSlug, globalProjectId, sortBy, sortOrder, projectById]);

  const selectableInvoices = useMemo(
    () => filteredAndSortedInvoices.filter(invoice => !isInvoiceLocked(invoice)),
    [filteredAndSortedInvoices]
  );

  const allVisibleSelected =
    selectableInvoices.length > 0 &&
    selectableInvoices.every(invoice => selectedIds.has(invoice.id));

  useEffect(() => {
    const deletableIds = new Set(
      invoices.filter(invoice => !isInvoiceLocked(invoice)).map(invoice => invoice.id)
    );
    setSelectedIds(prev => {
      const next = new Set(Array.from(prev).filter(id => deletableIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [invoices]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'draft':
        return '📝';
      case 'sent':
        return '📤';
      case 'paid':
        return '✅';
      case 'overdue':
        return '⚠️';
      default:
        return '📄';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'bg-slate-50 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900/20 dark:text-slate-400 dark:ring-slate-700';
      case 'sent':
        return 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:ring-blue-800';
      case 'paid':
        return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:ring-emerald-800';
      case 'overdue':
        return 'bg-red-50 text-red-700 ring-1 ring-red-200 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-800';
      default:
        return 'bg-slate-50 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900/20 dark:text-slate-400 dark:ring-slate-700';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'draft':
        return 'Borrador';
      case 'sent':
        return 'Enviada';
      case 'paid':
        return 'Pagada';
      case 'overdue':
        return 'Vencida';
      default:
        return 'Desconocido';
    }
  };

  const formatCurrency = (amount: number | string) => {
    const numericAmount = Number(amount || 0);
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(numericAmount);
  };

  const formatDate = (dateInput: Date | string | null | undefined) => {
    if (!dateInput) return '-';
    const raw = typeof dateInput === 'string' ? dateInput.trim() : '';
    const isoOnly = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (isoOnly) {
      const d = new Date(`${isoOnly[1]}T12:00:00Z`);
      if (!isNaN(d.getTime())) {
        return new Intl.DateTimeFormat('es-ES', { timeZone: 'UTC' }).format(d);
      }
    }
    const dateObj = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (isNaN(dateObj.getTime())) return '-';
    return new Intl.DateTimeFormat('es-ES').format(dateObj);
  };

  const handleToggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectableInvoices.forEach(invoice => next.delete(invoice.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        selectableInvoices.forEach(invoice => next.add(invoice.id));
        return next;
      });
    }
  };

  const handleToggleSelect = (invoiceId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(invoiceId)) {
        next.delete(invoiceId);
      } else {
        next.add(invoiceId);
      }
      return next;
    });
  };

  const handleDelete = async (invoiceId: string) => {
    if (
      !window.confirm(
        '¿Estás seguro de que quieres eliminar esta factura? Esta acción no se puede deshacer.'
      )
    ) {
      return;
    }
    await onDeleteInvoice?.(invoiceId);
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.delete(invoiceId);
      return next;
    });
  };

  const handleOpenBulkDeleteModal = () => {
    if (selectedIds.size === 0) return;
    setShowDeleteModal(true);
  };

  const handleCloseBulkDeleteModal = () => {
    if (isDeleting) return;
    setShowDeleteModal(false);
  };

  const handleBulkStatus = async (status: 'draft' | 'sent' | 'paid' | 'overdue') => {
    if (!onBulkUpdateStatus || selectedIds.size === 0) return;
    const labels: Record<string, string> = {
      paid: 'pagada',
      sent: 'enviada',
      draft: 'borrador',
      overdue: 'vencida'
    };
    if (
      !window.confirm(
        `¿Marcar ${selectedIds.size} factura(s) como ${labels[status] || status}?`
      )
    ) {
      return;
    }

    setIsUpdatingStatus(true);
    setStatusFeedback(null);
    setStatusErrors([]);

    try {
      const ids = Array.from(selectedIds);
      const result = await onBulkUpdateStatus(ids, status);
      if (result) {
        const updatedSet = new Set(result.updated);
        setSelectedIds(prev => {
          const next = new Set(Array.from(prev).filter(id => !updatedSet.has(id)));
          return next;
        });
        if (result.failed?.length) setStatusErrors(result.failed);
        setStatusFeedback(
          result.message ||
            `${result.updated.length} factura(s) actualizada(s)${
              result.failed?.length ? `, ${result.failed.length} con error` : ''
            }`
        );
      } else {
        setSelectedIds(new Set());
        setStatusFeedback('Estados actualizados correctamente');
      }
    } catch {
      setStatusFeedback('Error al actualizar el estado de las facturas seleccionadas');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (!onBulkDeleteInvoices || selectedIds.size === 0) return;

    setIsDeleting(true);
    setDeleteFeedback(null);
    setDeleteErrors([]);

    try {
      const ids = Array.from(selectedIds);
      const result = await onBulkDeleteInvoices(ids);

      if (result) {
        const deletedSet = new Set(result.deleted);
        setSelectedIds(prev => {
          const next = new Set(Array.from(prev).filter(id => !deletedSet.has(id)));
          return next;
        });
        if (result.failed?.length) {
          setDeleteErrors(result.failed);
        }
        setDeleteFeedback(
          result.message ||
            `${result.deleted.length} factura(s) eliminada(s)${
              result.failed?.length ? `, ${result.failed.length} con error` : ''
            }`
        );
      } else {
        setSelectedIds(new Set());
        setDeleteFeedback('Facturas eliminadas correctamente');
      }

      setShowDeleteModal(false);
    } catch {
      setDeleteFeedback('Error al eliminar las facturas seleccionadas');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSendEmail = async (invoice: Invoice) => {
    if (!invoice.client.email) {
      setEmailMessages(prev => ({
        ...prev,
        [invoice.id]: 'Cliente sin email configurado'
      }));
      return;
    }

    setSendingEmails(prev => new Set(prev).add(invoice.id));
    setEmailMessages(prev => ({ ...prev, [invoice.id]: '' }));

    try {
      const result = await emailService.sendInvoice(invoice, invoice.client);
      setEmailMessages(prev => ({
        ...prev,
        [invoice.id]: result.message
      }));
    } catch {
      setEmailMessages(prev => ({
        ...prev,
        [invoice.id]: 'Error al enviar el email'
      }));
    } finally {
      setSendingEmails(prev => {
        const newSet = new Set(prev);
        newSet.delete(invoice.id);
        return newSet;
      });
    }
  };

  const inputClassName =
    'w-full px-3 py-2 border border-[#E6E9F0] dark:border-[#30363d] rounded-lg bg-white dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] placeholder-[#8898aa] focus:outline-none focus:ring-2 focus:ring-[#635BFF]/20 focus:border-[#635BFF] transition-colors text-sm';

  const handleBulkAssignProject = async (projectId: string | null, projectName: string) => {
    if (!onBulkAssignProject || selectedIds.size === 0) return;
    setShowProjectDropdown(false);
    setIsAssigningProject(true);
    const ids = Array.from(selectedIds);
    try {
      const result = await onBulkAssignProject(ids, projectId);
      const msg = (result as any)?.message || `${ids.length} factura(s) asignadas a "${projectName}"`;
      toast.success(msg);
      setSelectedIds(new Set());
    } catch (err: any) {
      toast.error(err?.message || 'Error al asignar proyecto');
    } finally {
      setIsAssigningProject(false);
    }
  };

  const handleBulkAssignGroup = async (groupId: string, groupName: string) => {
    if (!onBulkAssignGroups || selectedIds.size === 0) return;
    setShowGroupDropdown(false);
    setIsAssigningGroup(true);
    const ids = Array.from(selectedIds);
    try {
      const result = await onBulkAssignGroups(ids, [groupId]);
      if (!result) {
        toast.error('No se pudo asignar el grupo');
        return;
      }
      if ((result as any).failed?.length) {
        toast.error((result as any).message || `Error al añadir al grupo "${groupName}"`);
      } else {
        toast.success((result as any).message || `${ids.length} factura(s) añadida(s) a "${groupName}"`);
        setSelectedIds(new Set());
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error al añadir facturas al grupo');
    } finally {
      setIsAssigningGroup(false);
    }
  };

  const handleExportXLSX = () => {
    if (filteredAndSortedInvoices.length === 0) {
      toast.error("No hay facturas para exportar");
      return;
    }
    const rows = filteredAndSortedInvoices.map((inv) => {
      const proj = inv.projectId ? projectById.get(inv.projectId) : null;
      return {
        "Número": inv.number,
        "Fecha": inv.date ? new Date(inv.date).toLocaleDateString("es-ES") : "",
        "Vencimiento": inv.dueDate ? new Date(inv.dueDate).toLocaleDateString("es-ES") : "",
        "Cliente": inv.client?.name || inv.clientId || "",
        "NIF/CIF": (inv.client as any)?.nif || "",
        "Proyecto": proj?.name || "",
        "Base imponible (€)": Number(inv.subtotal || 0).toFixed(2),
        "IVA (€)": Number(inv.ivaAmount || 0).toFixed(2),
        "IRPF (€)": Number(inv.irpfAmount || 0).toFixed(2),
        "Total (€)": Number(inv.total || 0).toFixed(2),
        "Estado": inv.status === "paid" ? "Pagada" : inv.status === "sent" ? "Enviada" : inv.status === "overdue" ? "Vencida" : "Borrador",
        "Método de pago": inv.paymentMethod || "",
        "Notas": (inv as any).notes || "",
      };
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const colWidths = Object.keys(rows[0] || {}).map((k) => ({
      wch: Math.max(k.length, ...rows.map((r) => String((r as any)[k] || "").length)) + 2,
    }));
    ws["!cols"] = colWidths;
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Facturas");
    const fileName = `facturas-${new Date().toISOString().slice(0,10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(`${rows.length} facturas exportadas`);
  };

  if (invoices.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#635BFF]/10 flex items-center justify-center mb-4">
          <DocumentTextIcon className="h-8 w-8 text-[#635BFF]" />
        </div>
        <h3 className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3] mb-2">
          No hay facturas todavía
        </h3>
        <p className="text-sm text-[#425466] dark:text-[#8b949e] mb-6 max-w-xs">
          Crea tu primera factura o importa facturas existentes para comenzar.
        </p>
        {onAddInvoice && (
          <button
            onClick={onAddInvoice}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#635BFF] text-white text-sm font-semibold hover:bg-[#4F46E5] shadow-sm transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Crear primera factura
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-[#0A2540] dark:text-[#e6edf3]">Facturas</h2>
          {activeProjectName ? (
            <p className="text-sm text-[#635BFF] font-medium mt-0.5">
              Proyecto: {activeProjectName}
            </p>
          ) : (
            <p className="text-sm text-[#8898aa]">
              {filteredAndSortedInvoices.length} de {invoices.length} facturas
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportXLSX}
            title="Exportar a Excel"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22] text-[#425466] dark:text-[#8b949e] text-sm font-medium hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] transition-colors"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span className="hidden sm:inline">Excel</span>
          </button>
          {onAddInvoice && (
            <button
              onClick={onAddInvoice}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#635BFF] text-white text-sm font-semibold hover:bg-[#4F46E5] shadow-sm transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              Nueva factura
            </button>
          )}
        </div>
      </div>

      {!globalProjectId && (
        <div className="flex flex-wrap gap-1.5">
          {PROJECT_FILTER_CHIPS.map((chip) => {
            const active = filterProjectSlug === chip.key;
            return (
              <button
                key={chip.key}
                type="button"
                onClick={() => setFilterProjectSlug(chip.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? 'bg-[#635BFF] text-white shadow-sm'
                    : 'bg-white dark:bg-[#161b22] border border-[#E6E9F0] dark:border-[#30363d] text-[#425466] dark:text-[#8b949e] hover:border-[#635BFF]/40 hover:text-[#635BFF]'
                }`}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      )}

      {invoiceGroups.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-[#8898aa] mr-1">Grupos:</span>
          <button
            type="button"
            onClick={() => setFilterGroupSlug('all')}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              filterGroupSlug === 'all'
                ? 'bg-[#0A2540] text-white dark:bg-[#e6edf3] dark:text-[#0d1117]'
                : 'bg-white dark:bg-[#161b22] border border-[#E6E9F0] dark:border-[#30363d] text-[#425466] dark:text-[#8b949e]'
            }`}
          >
            Todos
          </button>
          {invoiceGroups.map((g) => {
            const active = filterGroupSlug === g.slug;
            return (
              <button
                key={g.id}
                type="button"
                onClick={() => setFilterGroupSlug(active ? 'all' : g.slug)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  active ? 'text-white' : 'bg-white dark:bg-[#161b22] text-[#425466] dark:text-[#8b949e]'
                }`}
                style={
                  active
                    ? { backgroundColor: g.color || '#635BFF', borderColor: g.color || '#635BFF' }
                    : { borderColor: `${g.color || '#635BFF'}40`, color: g.color || '#635BFF' }
                }
              >
                {g.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <input
            type="text"
            placeholder="Buscar facturas..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className={inputClassName}
          />
        </div>
        <div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className={inputClassName}>
            <option value="all">Todos los estados</option>
            <option value="draft">Borrador</option>
            <option value="sent">Enviada</option>
            <option value="paid">Pagada</option>
            <option value="overdue">Vencida</option>
          </select>
        </div>
        <div>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className={inputClassName}>
            <option value="date">Ordenar por fecha</option>
            <option value="number">Por número</option>
            <option value="total">Por importe</option>
            <option value="client">Por cliente</option>
          </select>
        </div>
      </div>

      {/* Bulk actions bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        {(canDelete || onBulkUpdateStatus) && (
          <div className="flex flex-wrap items-center gap-2">
            {canDelete && (
              <label className="inline-flex items-center gap-2 text-sm text-[#425466] dark:text-[#8b949e] cursor-pointer">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={handleToggleSelectAllVisible}
                  disabled={selectableInvoices.length === 0}
                  className="h-4 w-4 rounded border-[#E6E9F0] accent-[#635BFF] disabled:opacity-50"
                />
                <span className="hidden sm:inline text-xs">
                  Todas ({selectableInvoices.length})
                </span>
              </label>
            )}

            {selectedIds.size > 0 && (
              <span className="text-xs font-medium text-[#635BFF] bg-[#635BFF]/10 px-2.5 py-1 rounded-full">
                {selectedIds.size} seleccionada(s)
              </span>
            )}

            {onBulkUpdateStatus && (
              <>
                <button
                  type="button"
                  onClick={() => handleBulkStatus('paid')}
                  disabled={selectedIds.size === 0 || isUpdatingStatus}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <CheckCircleIcon className="h-3.5 w-3.5" />
                  Pagada
                </button>
                <button
                  type="button"
                  onClick={() => handleBulkStatus('sent')}
                  disabled={selectedIds.size === 0 || isUpdatingStatus}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg border border-[#E6E9F0] dark:border-[#30363d] text-[#425466] dark:text-[#8b949e] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Enviada
                </button>
                <button
                  type="button"
                  onClick={() => handleBulkStatus('draft')}
                  disabled={selectedIds.size === 0 || isUpdatingStatus}
                  className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg border border-[#E6E9F0] dark:border-[#30363d] text-[#425466] dark:text-[#8b949e] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Borrador
                </button>
              </>
            )}

            {/* Bulk assign project */}
            {onBulkAssignProject && projects.length > 0 && (
              <div className="relative" ref={projectDropdownRef}>
                <button
                  type="button"
                  disabled={selectedIds.size === 0 || isAssigningProject}
                  onClick={() => setShowProjectDropdown(v => !v)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22] text-[#425466] dark:text-[#8b949e] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isAssigningProject ? (
                    <span className="h-3.5 w-3.5 border-2 border-[#635BFF] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <DocumentTextIcon className="h-3.5 w-3.5" />
                  )}
                  Asignar proyecto
                  <span className="ml-0.5 text-[#8898aa]">▾</span>
                </button>

                {showProjectDropdown && (
                  <div className="absolute left-0 top-full mt-1 z-50 w-52 rounded-xl border border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22] shadow-lg py-1 animate-fadeIn">
                    <p className="px-3 py-1.5 text-[10px] font-semibold text-[#8898aa] uppercase tracking-wider">
                      Asignar {selectedIds.size} factura(s) a…
                    </p>
                    {projects.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => handleBulkAssignProject(p.id, p.name)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#0A2540] dark:text-[#e6edf3] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] text-left transition-colors"
                      >
                        {p.color && (
                          <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                        )}
                        {p.name}
                      </button>
                    ))}
                    <div className="border-t border-[#F6F9FC] dark:border-[#21262d] mt-1 pt-1">
                      <button
                        type="button"
                        onClick={() => handleBulkAssignProject(null, 'sin proyecto')}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#8898aa] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] text-left transition-colors"
                      >
                        <XMarkIcon className="h-3.5 w-3.5" />
                        Sin proyecto
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Bulk assign invoice groups */}
            {onBulkAssignGroups && invoiceGroups.length > 0 && (
              <div className="relative" ref={groupDropdownRef}>
                <button
                  type="button"
                  disabled={selectedIds.size === 0 || isAssigningGroup}
                  onClick={() => setShowGroupDropdown(v => !v)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22] text-[#425466] dark:text-[#8b949e] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isAssigningGroup ? (
                    <span className="h-3.5 w-3.5 border-2 border-[#635BFF] border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <DocumentTextIcon className="h-3.5 w-3.5" />
                  )}
                  Añadir a grupo
                  <span className="ml-0.5 text-[#8898aa]">▾</span>
                </button>

                {showGroupDropdown && (
                  <div className="absolute left-0 top-full mt-1 z-50 w-56 rounded-xl border border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22] shadow-lg py-1 animate-fadeIn">
                    <p className="px-3 py-1.5 text-[10px] font-semibold text-[#8898aa] uppercase tracking-wider">
                      Añadir {selectedIds.size} factura(s) a…
                    </p>
                    {invoiceGroups.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => handleBulkAssignGroup(g.id, g.name)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#0A2540] dark:text-[#e6edf3] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] text-left transition-colors"
                      >
                        <span
                          className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: g.color || '#635BFF' }}
                        />
                        {g.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {canDelete && onBulkDeleteInvoices && (
              <button
                type="button"
                onClick={handleOpenBulkDeleteModal}
                disabled={selectedIds.size === 0 || isDeleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <TrashIcon className="h-3.5 w-3.5" />
                Eliminar
              </button>
            )}
          </div>
        )}

        <button
          onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          className="px-3 py-1.5 text-xs font-medium text-[#425466] dark:text-[#8b949e] border border-[#E6E9F0] dark:border-[#30363d] rounded-lg hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] transition-colors sm:ml-auto"
        >
          {sortOrder === 'asc' ? '↑ Ascendente' : '↓ Descendente'}
        </button>
      </div>

      {(statusFeedback || statusErrors.length > 0) && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${statusErrors.length > 0 ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/40 dark:bg-amber-900/10 dark:text-amber-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/40 dark:bg-emerald-900/10 dark:text-emerald-300'}`}>
          {statusFeedback && <p>{statusFeedback}</p>}
          {statusErrors.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {statusErrors.map(err => <li key={err.id} className="text-xs">{err.message || err.error}</li>)}
            </ul>
          )}
        </div>
      )}

      {(deleteFeedback || deleteErrors.length > 0) && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${deleteErrors.length > 0 ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/40 dark:bg-amber-900/10 dark:text-amber-300' : 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-800/40 dark:bg-emerald-900/10 dark:text-emerald-300'}`}>
          {deleteFeedback && <p>{deleteFeedback}</p>}
          {deleteErrors.length > 0 && (
            <ul className="mt-1.5 space-y-0.5">
              {deleteErrors.map(err => <li key={err.id} className="text-xs">{err.message || err.error} (ID: {err.id})</li>)}
            </ul>
          )}
        </div>
      )}

      {/* Invoice list */}
      <div className="bg-white dark:bg-[#161b22] rounded-xl border border-[#E6E9F0] dark:border-[#21262d] shadow-stripe overflow-hidden">
        <ul className="divide-y divide-[#F6F9FC] dark:divide-[#21262d]">
          {filteredAndSortedInvoices.map(invoice => {
            const locked = isInvoiceLocked(invoice);
            const isSelected = selectedIds.has(invoice.id);

            return (
              <li
                key={invoice.id}
                className={`px-5 py-3.5 transition-colors duration-150 hover:bg-[#F6F9FC] dark:hover:bg-[#21262d]/50 ${
                  isSelected ? 'bg-[#635BFF]/5 dark:bg-[#635BFF]/10' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {(canDelete || onBulkUpdateStatus) && (
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(invoice.id)}
                        disabled={locked}
                        className="h-4 w-4 shrink-0 rounded border-[#E6E9F0] accent-[#635BFF] disabled:opacity-40 disabled:cursor-not-allowed"
                        title={locked ? 'Factura bloqueada (presentada a AEAT)' : 'Seleccionar factura'}
                      />
                    )}

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-[#0A2540] dark:text-[#e6edf3]">
                          #{invoiceNumberLabel(invoice)}
                        </p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                          {getStatusText(invoice.status)}
                        </span>
                        {locked && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:ring-amber-800">
                            Bloqueada
                          </span>
                        )}
                        {showProjectBadges && invoice.projectId && projectById.get(invoice.projectId) && (
                          <span
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              border: `1px solid ${projectById.get(invoice.projectId!)?.color || '#635BFF'}40`,
                              color: projectById.get(invoice.projectId!)?.color || '#635BFF',
                              backgroundColor: `${projectById.get(invoice.projectId!)?.color || '#635BFF'}10`,
                            }}
                          >
                            {projectById.get(invoice.projectId!)?.name}
                          </span>
                        )}
                        {(invoice.groups || []).map((g) => (
                          <span
                            key={g.id}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                            style={{
                              border: `1px solid ${g.color || '#635BFF'}40`,
                              color: g.color || '#635BFF',
                              backgroundColor: `${g.color || '#635BFF'}10`,
                            }}
                          >
                            {g.name}
                          </span>
                        ))}
                      </div>

                      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-[#425466] dark:text-[#8b949e]">
                        <span>{invoice.client.name}</span>
                        <span className="text-[#E6E9F0] dark:text-[#30363d]">·</span>
                        <span>{formatDate(invoice.date as string | Date)}</span>
                        <span className="text-[#E6E9F0] dark:text-[#30363d]">·</span>
                        <span className="font-semibold text-[#0A2540] dark:text-[#e6edf3]">{formatCurrency(Number(invoice.total || 0))}</span>
                      </div>

                      {invoice.description && (
                        <p className="mt-0.5 text-xs text-[#8898aa] truncate max-w-sm">
                          {invoice.description}
                        </p>
                      )}

                      {emailMessages[invoice.id] && (
                        <p className={`mt-1.5 text-xs ${
                          emailMessages[invoice.id].includes('exitosamente')
                            ? 'text-emerald-600'
                            : emailMessages[invoice.id].includes('Error')
                              ? 'text-red-600'
                              : 'text-amber-600'
                        }`}>
                          {emailMessages[invoice.id]}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-0.5 shrink-0">
                    {onViewInvoice && (
                      <button
                        onClick={() => onViewInvoice(invoice)}
                        className="p-1.5 text-[#8898aa] hover:text-[#635BFF] hover:bg-[#635BFF]/10 rounded-lg transition-colors"
                        title="Ver factura"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                    )}

                    <button
                      onClick={() => setPdfPreview({ id: invoice.id, number: invoice.number })}
                      className="p-1.5 text-[#8898aa] hover:text-[#635BFF] hover:bg-[#635BFF]/10 rounded-lg transition-colors"
                      title="Vista previa PDF"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => handleDownloadPdf(invoice)}
                      disabled={downloadingPdfId === invoice.id}
                      className="p-1.5 text-[#8898aa] hover:text-[#635BFF] hover:bg-[#635BFF]/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Descargar PDF"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>

                    <button
                      onClick={() => handleSendEmail(invoice)}
                      disabled={sendingEmails.has(invoice.id)}
                      className="p-1.5 text-[#8898aa] hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Enviar por email"
                    >
                      <EnvelopeIcon className="h-4 w-4" />
                    </button>

                    {onMarkAsPaid && invoice.status !== 'paid' && (
                      <button
                        onClick={() => onMarkAsPaid(invoice.id)}
                        className="p-1.5 text-[#8898aa] hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                        title="Marcar como pagada"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </button>
                    )}

                    {onEditInvoice && (
                      <button
                        onClick={() => onEditInvoice(invoice)}
                        className="p-1.5 text-[#8898aa] hover:text-[#635BFF] hover:bg-[#635BFF]/10 rounded-lg transition-colors"
                        title="Editar factura"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    )}

                    {canDelete && onDeleteInvoice && !locked && (
                      <button
                        onClick={() => handleDelete(invoice.id)}
                        className="p-1.5 text-[#8898aa] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Eliminar factura"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Summary bar */}
      <div className="rounded-xl bg-white dark:bg-[#161b22] border border-[#E6E9F0] dark:border-[#21262d] p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-xs font-medium text-[#8898aa] uppercase tracking-wider">Total</p>
            <p className="text-xl font-semibold text-[#0A2540] dark:text-[#e6edf3] tabular-nums mt-0.5">{invoices.length}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-[#8898aa] uppercase tracking-wider">Ingresos</p>
            <p className="text-xl font-semibold text-emerald-600 tabular-nums mt-0.5">
              {formatCurrency(invoices.reduce((sum, inv) => sum + Number(inv.total || 0), 0))}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-[#8898aa] uppercase tracking-wider">Pagadas</p>
            <p className="text-xl font-semibold text-[#635BFF] tabular-nums mt-0.5">
              {invoices.filter(inv => inv.status === 'paid').length}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-[#8898aa] uppercase tracking-wider">Pendientes</p>
            <p className="text-xl font-semibold text-amber-600 tabular-nums mt-0.5">
              {invoices.filter(inv => inv.status !== 'paid').length}
            </p>
          </div>
        </div>
      </div>

      {pdfPreview && (
        <InvoicePdfPreviewModal
          invoiceId={pdfPreview.id}
          invoiceNumber={pdfPreview.number}
          open={!!pdfPreview}
          onClose={() => setPdfPreview(null)}
        />
      )}

      {/* Bulk delete modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <button
            type="button"
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={handleCloseBulkDeleteModal}
            aria-label="Cerrar"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-delete-title"
            className="relative w-full max-w-md rounded-2xl border border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] shadow-stripe-lg p-6"
          >
            <button
              type="button"
              onClick={handleCloseBulkDeleteModal}
              disabled={isDeleting}
              className="absolute top-4 right-4 p-1.5 text-[#8898aa] hover:text-[#425466] rounded-lg hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] disabled:opacity-50 transition-colors"
              aria-label="Cerrar modal"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>

            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 dark:bg-red-900/20">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div className="min-w-0 flex-1 pr-6">
                <h3
                  id="bulk-delete-title"
                  className="text-base font-semibold text-[#0A2540] dark:text-[#e6edf3]"
                >
                  Eliminar facturas seleccionadas
                </h3>
                <p className="mt-2 text-sm text-[#425466] dark:text-[#8b949e]">
                  ¿Estás seguro de que quieres eliminar{' '}
                  <span className="font-semibold text-[#0A2540] dark:text-[#e6edf3]">
                    {selectedIds.size}
                  </span>{' '}
                  factura(s)? Esta acción no se puede deshacer.
                </p>
                <p className="mt-2 text-xs text-[#8898aa]">
                  Las facturas bloqueadas (presentadas a AEAT) no se incluyen en la selección.
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
              <button
                type="button"
                onClick={handleCloseBulkDeleteModal}
                disabled={isDeleting}
                className="inline-flex justify-center px-4 py-2 text-sm font-medium rounded-lg border border-[#E6E9F0] dark:border-[#30363d] text-[#425466] dark:text-[#8b949e] bg-white dark:bg-[#161b22] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] disabled:opacity-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmBulkDelete}
                disabled={isDeleting}
                className="inline-flex justify-center items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                {isDeleting ? 'Eliminando…' : 'Eliminar definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
